import React, { Fragment, useCallback, useContext } from 'react';
import { animated, to } from '@react-spring/web';

import type { TPageContentItem } from '../../types/wysiwyg_editor';
import type { SprungRect } from '../../types/misc';
import type {
  SnappableLocations,
  AxisSnappableLocationData
} from '../../hooks/wysiwyg_editor/useSnappableLocations';
import { WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';
import usePageContentItemCount from '@src/hooks/wysiwyg_editor/usePageContentItemCount';

interface IProps {
  contentItem: TPageContentItem;
  frameLocation: SprungRect;
  snappableLocations: SnappableLocations;
}

const SNAP_LINE_THICKNESS = 2;
// We allow a very small deviance in the actual element location and the snap
// location because sometimes they are very slightly different even when
// "snapped" together.
const DISPLAY_WITHIN_PERCENT = 0.001;

export default function SnapLines({
  frameLocation,
  snappableLocations
}: IProps): React.ReactElement {
  const pageContentItemCount = usePageContentItemCount();
  const { renderRect: stencilRenderRect, viewbox } = useContext(
    WysiwygEditorStateContext
  );

  const findClosestXWithin = useCallback(
    (
      viewboxPixelLocation,
      withinPercentage
    ): AxisSnappableLocationData | null => {
      const keys = Object.keys(snappableLocations.x).map(parseFloat);
      const viewboxPercentageLocation = viewboxPixelLocation / viewbox.width;

      if (keys.length === 0) return null;

      // sort keys by how close they are to viewboxPercentageLocation
      keys.sort(
        (a, b) =>
          Math.abs(viewboxPercentageLocation - a) -
          Math.abs(viewboxPercentageLocation - b)
      );

      if (Math.abs(viewboxPercentageLocation - keys[0]) <= withinPercentage) {
        return snappableLocations.x[keys[0]];
      }

      return null;
    },
    [snappableLocations.x, viewbox.width]
  );

  const findClosestYWithin = useCallback(
    (
      viewboxPixelLocation,
      withinPercentage
    ): AxisSnappableLocationData | null => {
      const keys = Object.keys(snappableLocations.y).map(parseFloat);
      const viewboxPercentageLocation = viewboxPixelLocation / viewbox.height;

      if (keys.length === 0) return null;

      // sort keys by how close they are to viewboxPercentageLocation
      keys.sort(
        (a, b) =>
          Math.abs(viewboxPercentageLocation - a) -
          Math.abs(viewboxPercentageLocation - b)
      );

      if (Math.abs(viewboxPercentageLocation - keys[0]) <= withinPercentage) {
        return snappableLocations.y[keys[0]];
      }

      return null;
    },
    [snappableLocations.y, viewbox.height]
  );

  const verticalTop = useCallback(
    (y: number, xKey: number) => {
      const matchingLocation = findClosestXWithin(xKey, DISPLAY_WITHIN_PERCENT);
      if (!matchingLocation) return 0;
      const yPercent = y / viewbox.height;
      const topPercent = Math.min(yPercent, matchingLocation.minLocation);
      return topPercent * stencilRenderRect.height;
    },
    [findClosestXWithin, stencilRenderRect.height, viewbox.height]
  );

  const verticalBottom = useCallback(
    (y: number, height: number, xKey: number) => {
      const matchingLocation = findClosestXWithin(xKey, DISPLAY_WITHIN_PERCENT);
      if (!matchingLocation) return 0;
      const yPercent = y / viewbox.height;
      const heightPercent = height / viewbox.height;
      const bottomPercent = Math.max(
        yPercent + heightPercent,
        matchingLocation.maxLocation
      );
      return (1 - bottomPercent) * stencilRenderRect.height;
    },
    [findClosestXWithin, stencilRenderRect.height, viewbox.height]
  );

  const horizontalLeft = useCallback(
    (x: number, yKey: number) => {
      const matchingLocation = findClosestYWithin(yKey, DISPLAY_WITHIN_PERCENT);
      if (!matchingLocation) return 0;
      const xPercent = x / viewbox.width;
      const leftPercent = Math.min(xPercent, matchingLocation.minLocation);
      return leftPercent * stencilRenderRect.width;
    },
    [findClosestYWithin, stencilRenderRect.width, viewbox.width]
  );

  const horizontalRight = useCallback(
    (x: number, width: number, yKey: number) => {
      const matchingLocation = findClosestYWithin(yKey, DISPLAY_WITHIN_PERCENT);
      if (!matchingLocation) return 0;
      const xPercent = x / viewbox.width;
      const widthPercent = width / viewbox.width;
      const rightPercent = Math.max(
        xPercent + widthPercent,
        matchingLocation.maxLocation
      );
      return (1 - rightPercent) * stencilRenderRect.width;
    },
    [findClosestYWithin, stencilRenderRect.width, viewbox.width]
  );

  const borderStyle = useCallback(
    (axis: string, key: number) => {
      const matchingLocation =
        axis === 'x'
          ? findClosestXWithin(key, DISPLAY_WITHIN_PERCENT)
          : findClosestYWithin(key, DISPLAY_WITHIN_PERCENT);

      if (!matchingLocation) return 'solid';

      return /dash/.test(matchingLocation.edgeType) ? 'dashed' : 'solid';
    },
    [findClosestXWithin, findClosestYWithin]
  );

  return (
    <Fragment>
      {/* Left vertical */}
      <animated.div
        style={{
          position: 'absolute',
          top: to([frameLocation.x, frameLocation.y], (x, y) =>
            verticalTop(y, x)
          ),
          left: to(
            [frameLocation.x],
            (x) => (x / viewbox.width) * stencilRenderRect.width
          ),
          bottom: to(
            [frameLocation.x, frameLocation.y, frameLocation.height],
            (x, y, height) => verticalBottom(y, height, x)
          ),
          width: SNAP_LINE_THICKNESS,
          display: to([frameLocation.x], (x) =>
            findClosestXWithin(x, DISPLAY_WITHIN_PERCENT) ? 'block' : 'none'
          ),
          borderLeft: to(
            [frameLocation.x],
            (x) => `2px #ff1a36 ${borderStyle('x', x)}`
          ),
          zIndex: pageContentItemCount + 2
        }}
      />

      {/* Right vertical */}
      <animated.div
        style={{
          position: 'absolute',
          top: to(
            [frameLocation.x, frameLocation.y, frameLocation.width],
            (x, y, width) => verticalTop(y, x + width)
          ),
          left: to(
            [frameLocation.x, frameLocation.width],
            (x, width) =>
              ((x + width) / viewbox.width) * stencilRenderRect.width -
              SNAP_LINE_THICKNESS
          ),
          bottom: to(
            [
              frameLocation.x,
              frameLocation.y,
              frameLocation.height,
              frameLocation.width
            ],
            (x, y, height, width) => verticalBottom(y, height, x + width)
          ),
          width: SNAP_LINE_THICKNESS,
          display: to([frameLocation.x, frameLocation.width], (x, width) =>
            findClosestXWithin(x + width, DISPLAY_WITHIN_PERCENT)
              ? 'block'
              : 'none'
          ),
          borderRight: to(
            [frameLocation.x, frameLocation.width],
            (x, width) => `2px #ff1a36 ${borderStyle('x', x + width)}`
          ),
          zIndex: pageContentItemCount + 2
        }}
      />

      {/* Center vertical */}
      <animated.div
        style={{
          position: 'absolute',
          top: to(
            [frameLocation.x, frameLocation.y, frameLocation.width],
            (x, y, width) => verticalTop(y, x + width / 2)
          ),
          left: to(
            [frameLocation.x, frameLocation.width],
            (x, width) =>
              ((x + width / 2) / viewbox.width) * stencilRenderRect.width -
              SNAP_LINE_THICKNESS / 2
          ),
          bottom: to(
            [
              frameLocation.x,
              frameLocation.y,
              frameLocation.height,
              frameLocation.width
            ],
            (x, y, height, width) => verticalBottom(y, height, x + width / 2)
          ),
          width: SNAP_LINE_THICKNESS,
          display: to([frameLocation.x, frameLocation.width], (x, width) =>
            findClosestXWithin(x + width / 2, DISPLAY_WITHIN_PERCENT)
              ? 'block'
              : 'none'
          ),
          borderLeft: to(
            [frameLocation.x, frameLocation.width],
            (x, width) => `2px #ff1a36 ${borderStyle('x', x + width / 2)}`
          ),
          zIndex: pageContentItemCount + 2
        }}
      />

      {/* Top horizontal */}
      <animated.div
        style={{
          position: 'absolute',
          top: to(
            [frameLocation.y],
            (y) => (y / viewbox.height) * stencilRenderRect.height
          ),
          left: to([frameLocation.x, frameLocation.y], (x, y) =>
            horizontalLeft(x, y)
          ),
          height: SNAP_LINE_THICKNESS,
          right: to(
            [frameLocation.x, frameLocation.y, frameLocation.width],
            (x, y, width) => horizontalRight(x, width, y)
          ),
          display: to([frameLocation.y], (y) =>
            findClosestYWithin(y, DISPLAY_WITHIN_PERCENT) ? 'block' : 'none'
          ),
          borderTop: to(
            [frameLocation.y],
            (y) => `2px #ff1a36 ${borderStyle('y', y)}`
          ),
          zIndex: pageContentItemCount + 2
        }}
      />

      {/* Middle horizontal */}
      <animated.div
        style={{
          position: 'absolute',
          top: to(
            [frameLocation.y, frameLocation.height],
            (y, height) =>
              ((y + height / 2) / viewbox.height) * stencilRenderRect.height -
              SNAP_LINE_THICKNESS / 2
          ),
          left: to(
            [frameLocation.x, frameLocation.y, frameLocation.height],
            (x, y, height) => horizontalLeft(x, y + height / 2)
          ),
          height: SNAP_LINE_THICKNESS,
          right: to(
            [
              frameLocation.x,
              frameLocation.y,
              frameLocation.width,
              frameLocation.height
            ],
            (x, y, width, height) => horizontalRight(x, width, y + height / 2)
          ),
          display: to([frameLocation.y, frameLocation.height], (y, height) =>
            findClosestYWithin(y + height / 2, DISPLAY_WITHIN_PERCENT)
              ? 'block'
              : 'none'
          ),
          borderTop: to(
            [frameLocation.y, frameLocation.height],
            (y, height) => `2px #ff1a36 ${borderStyle('y', y + height / 2)}`
          ),
          zIndex: pageContentItemCount + 2
        }}
      />

      {/* Bottom horizontal */}
      <animated.div
        style={{
          position: 'absolute',
          top: to(
            [frameLocation.y, frameLocation.height],
            (y, height) =>
              ((y + height) / viewbox.height) * stencilRenderRect.height -
              SNAP_LINE_THICKNESS
          ),
          left: to(
            [frameLocation.x, frameLocation.y, frameLocation.height],
            (x, y, height) => horizontalLeft(x, y + height)
          ),
          height: SNAP_LINE_THICKNESS,
          right: to(
            [
              frameLocation.x,
              frameLocation.y,
              frameLocation.width,
              frameLocation.height
            ],
            (x, y, width, height) => horizontalRight(x, width, y + height)
          ),
          display: to([frameLocation.y, frameLocation.height], (y, height) =>
            findClosestYWithin(y + height, DISPLAY_WITHIN_PERCENT)
              ? 'block'
              : 'none'
          ),
          borderBottom: to(
            [frameLocation.y, frameLocation.height],
            (y, height) => `2px #ff1a36 ${borderStyle('y', y + height)}`
          ),
          zIndex: pageContentItemCount + 2
        }}
      />
    </Fragment>
  );
}
